import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ideaService } from '../../../../services/ideas.service';
import Button from '../../../UI/Button/Button';
import Alert from '../../../UI/Alert/Alert';
import styles from '../../IdeasCreateForm/IdeasCreateForm.module.css';
import { toast } from 'react-toastify';

function IdeaFileUpdateForm({ isOpen, onClose, onSuccess, ideaId, locked = false, onLocked }) {
    const { t } = useTranslation('common');
    const { t: tValidation } = useTranslation('validation');

    const [file, setFile] = useState(null);
    const [note, setNote] = useState({ value: '', error: null });
    const [fileError, setFileError] = useState(null);
    const [generalError, setGeneralError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setFileError(null);
        setGeneralError(null);

        if (!selectedFile) {
            setFile(null);
            return;
        }

        if (selectedFile.type !== 'application/pdf') {
            setFileError(tValidation('file_must_be_pdf'));
            setFile(null);
            e.target.value = '';
            return;
        }

        if (selectedFile.size > 10 * 1024 * 1024) { // 10MB
            setFileError(tValidation('max_file_size_exceeded'));
            setFile(null);
            e.target.value = '';
            return;
        }

        setFile(selectedFile);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!file) {
            setFileError(tValidation('file_is_required'));
            return;
        }

        setIsLoading(true);
        setGeneralError(null);

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('note', note.value);

            const response = await ideaService.updateIdeaFile(ideaId, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.status === 'success') {
                toast.success(tValidation('file_updated_successfully'));
                if (onSuccess) {
                    onSuccess();
                }
                onClose();
            }
        } catch (error) {
            if (error?.status === 403 && error?.payload?.error === 'editing_is_closed_for_this_event') {
                toast.error(tValidation('editing_is_closed_for_this_event'));
                onLocked?.();
                onClose();
                return;
            }
            const errorEntity = error?.payload?.error;
            if (typeof errorEntity === 'string') {
                const message = tValidation(errorEntity);
                setGeneralError(message);
                toast.error(message);
            } else {
                const message = error.message || 'Failed to update file';
                setGeneralError(message);
                toast.error(message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles.overlay}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.title}>{t('ideas.pdf.update_modal_title')}</h2>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        disabled={isLoading}
                        type="button"
                    >
                        ×
                    </button>
                </div>
                <form className={styles.form} onSubmit={handleSubmit}>
                    {locked && (
                        <Alert type="error">
                            <p>{tValidation('editing_is_closed_for_this_event')}</p>
                        </Alert>
                    )}
                    {generalError && !locked && (
                        <Alert type="error">
                            <p>{generalError}</p>
                        </Alert>
                    )}

                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t('ideas.pdf.file_label')}</label>
                        <div className={styles.fileInputWrapper}>
                            <input
                                type="file"
                                accept=".pdf"
                                onChange={handleFileChange}
                                className={styles.fileInputHidden}
                                id="file-update"
                                disabled={locked}
                            />
                            <span className={styles.fileName}>
                                {file ? file.name : t('ideas.file_placeholder')}
                            </span>
                            <label htmlFor="file-update" className={styles.fileButton}>
                                {t('ideas.file_button')}
                            </label>
                        </div>
                        {fileError && <p className={styles.errorText}>{fileError}</p>}
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t('ideas.pdf.note_label')}</label>
                        <textarea
                            className={`${styles.textarea} ${note.error ? styles.textareaError : ''}`}
                            placeholder={t('ideas.pdf.note_placeholder')}
                            value={note.value}
                            onChange={(e) => {
                                const val = e.target.value;
                                let error = null;
                                if (val.length > 300) {
                                    error = { code: 'note_too_long', params: { maximum: 300 } };
                                }
                                setNote({ value: val, error });
                            }}
                            rows={4}
                            disabled={locked}
                        />
                        <div className={`${styles.charCount} ${note.value.length > 300 ? styles.charCountError : ''}`}>
                            {note.value.length} / 300
                        </div>
                        {note.error && (
                            <p className={styles.errorText}>
                                {
                                    tValidation(note.error?.code || '').toString().replace('{{toReplace}}', note.error?.params?.maximum || note.error?.params?.min)
                                }
                            </p>
                        )}
                    </div>

                    <div className={styles.buttons}>
                        <Button
                            variant="secondary"
                            type="button"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            {t('ideas.cancel')}
                        </Button>
                        {!locked && (
                            <Button variant="primary" type="submit" disabled={isLoading}>
                                {t('ideas.pdf.update_button')}
                            </Button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}

export default IdeaFileUpdateForm;
